import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import {
	fetchDetailedStaffInformation,
	fetchProxyUser,
	fetchSuperManagers,
	fetchAccountCoordinator,
} from 'pages/AnnualUserAccessReview/_api';
import { STATUS_TYPES } from 'pages/AnnualUserAccessReview/_const';
import { UserContext } from 'context/User/UserContext';
import { isDateWithinNDays } from 'pages/AnnualUserAccessReview/_util';
import WithLoadingHandler from 'hoc/withLoadingHandler/withLoadingHandler';

/**
 * Higher-order component to inject `setIsLoading` function into wrapped component.
 * Renders either the component or a Skeleton loading animation.
 * @param {React.ReactNode} WrappedComponent component to be injected
 * @returns wrapped version of `WrappedComponent`
 */
const withQueryCheck = WrappedComponent => {
	const HOC = props => {
		/**
		 * @type {import('context/User/UserContext').ContextUser}
		 */
		const contextUser = useContext(UserContext);
		const user =
			contextUser.userInfo.impersonationProfile ??
			contextUser.userInfo.userProfile;

		const location = useLocation();
		const userIdsFromParams = new URLSearchParams(location.search).get(
			'esoFiId',
		);
		const userIds = userIdsFromParams
			?.split(',')
			.map(id => {
				const num = Number(id);
				if (isNaN(num)) {
					return undefined;
				}
				return num;
			})
			.filter(id => typeof id === 'number');
		const [sUserValuesFromParams, setUserValuesFromParams] = useState([]);
		const [sLoadingUserInfo, setLoadingUserInfo] = useState(true);
		const [sLoadingSupervisorInfo, setLoadingSupervisorInfo] =
			useState(true);
		const [sOnBehalfOfManager, setOnBehalfOfManager] = useState(user);
		const [sMessages, setMessages] = useState([]);

		useEffect(() => {
			let active = true;

			async function fetchApi() {
				try {
					if (
						sUserValuesFromParams &&
						sUserValuesFromParams.length > 0
					) {
						if (
							!sUserValuesFromParams.some(
								u => u.supervisorFiId === user.ID,
							)
						) {
							const responses = await Promise.all(
								sUserValuesFromParams?.map(user =>
									fetchSuperManagers(user.fiId),
								),
							);
							const supermanagerMatch = responses.every(
								response =>
									response.some(r => r.fiId === user.ID),
							);
							if (supermanagerMatch) {
								const proxyUser = await fetchProxyUser(
									sUserValuesFromParams[0].supervisorFiId,
								);
								setOnBehalfOfManager(proxyUser);
								setMessages([
									{
										type: 'info',
										message:
											'You are acting as proxy as you review for ' +
											proxyUser.Name,
									},
								]);
							} else {
								//if logged in user is not in super-managers list, check the account admins list
								const adminResponses = await Promise.all(
									sUserValuesFromParams?.map(user =>
										fetchAccountCoordinator(user.fiId),
									),
								);
								const adminMatch = adminResponses.every(
									response => response.fiId === user.ID,
								);
								if (!adminMatch) {
									setMessages([
										{
											type: 'warning',
											message:
												'You are not permitted to perform this action on user(s) specified in the URL query parameters.',
										},
									]);
									setUserValuesFromParams([]);
								}
							}

							setLoadingSupervisorInfo(false);
						} else {
							setLoadingSupervisorInfo(false);
						}
					}
				} catch (err) {
					setMessages([
						{
							type: 'warning',
							message:
								'Issue when checking management organization chart permissions.',
						},
					]);
					setLoadingSupervisorInfo(false);
				}
			}

			if (
				active &&
				user &&
				!sLoadingUserInfo &&
				sLoadingSupervisorInfo &&
				sUserValuesFromParams.length > 0
			) {
				fetchApi();
			}

			return () => {
				active = false;
			};
		}, [
			sUserValuesFromParams,
			sLoadingUserInfo,
			sLoadingSupervisorInfo,
			user,
		]);

		useEffect(() => {
			let active = true;

			async function fetchApi() {
				try {
					if (userIds && userIds.length > 0) {
						const responses = await Promise.all(
							userIds?.map(id =>
								fetchDetailedStaffInformation(id),
							),
						);
						if (responses.length > 0) {
							setUserValuesFromParams(
								responses.map(dataset => {
									const { expiresSoon, difference } =
										isDateWithinNDays(
											new Date(
												dataset.dateValidationExpires,
											),
											30,
										);

									const compliance = dataset?.isCompliant
										? expiresSoon
											? STATUS_TYPES.VALID_EXPIRING_SOON
											: STATUS_TYPES.VALID
										: STATUS_TYPES.OVERDUE;

									return {
										...dataset,
										isSelected: false,
										id: dataset.fiId,
										name: dataset.lastName
											? dataset.lastName +
												', ' +
												dataset.firstName
											: null,
										status: compliance,
										daysUntilExpiration: difference,
										department: dataset.departmentName,
										jobTitle: dataset.jobTitle,
										companyName: dataset.companyName,
									};
								}),
							);
						}
						setLoadingUserInfo(false);
					} else {
						setLoadingUserInfo(false);
					}
				} catch (err) {
					setMessages([
						{
							type: 'warning',
							message:
								'The user ID specified in the query parameters is invalid.',
						},
					]);
					setLoadingUserInfo(false);
				}
			}

			if (active && sLoadingUserInfo) {
				fetchApi();
			}

			return () => {
				active = false;
			};
		}, [userIds, sLoadingUserInfo]);

		return (
			<WithLoadingHandler
				isLoading={sLoadingUserInfo && sLoadingSupervisorInfo}
				showChildren={!(sLoadingUserInfo && sLoadingSupervisorInfo)}
			>
				{!(sLoadingUserInfo && sLoadingSupervisorInfo) && (
					<WrappedComponent
						{...props}
						initialValuesFromParams={sUserValuesFromParams}
						loggedInUser={user}
						targetUser={sOnBehalfOfManager}
						messages={sMessages}
					/>
				)}
			</WithLoadingHandler>
		);
	};
	return HOC;
};

export default withQueryCheck;
